import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { getDatabases } from "../api/admin";
import toast from "react-hot-toast";
import {
  Database,
  RefreshCw,
  FileText,
  Search,
  TrendingUp,
  AlertCircle,
} from "lucide-react";

const DatabaseDashboard = () => {
  const { adminToken } = useAuth();
  const navigate = useNavigate();
  const [databaseInfo, setDatabaseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchDatabases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getDatabases(adminToken);
      if (response.success) {
        setDatabaseInfo(response);
      } else {
        toast.error("Failed to load database information");
      }
    } catch (error) {
      console.error("Error fetching databases:", error);
      const isBackendDown =
        error.code === "ERR_NETWORK" ||
        error.message === "Network Error" ||
        !error.response;
      toast.error(
        isBackendDown
          ? "Backend is not running. Use: cd admin && npm start"
          : "Failed to load database information"
      );
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) {
      fetchDatabases();
    }
  }, [adminToken, fetchDatabases]);

  const filteredCollections = databaseInfo?.collections?.filter((collection) =>
    collection.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const totalDocuments = databaseInfo?.collections?.reduce(
    (sum, collection) => {
      if (typeof collection.count === "number") {
        return sum + collection.count;
      }
      return sum;
    },
    0
  ) || 0;

  const formatNumber = (num) => {
    if (typeof num !== "number") return num;
    return num.toLocaleString();
  };

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-red-600 to-red-700 dark:from-red-400 dark:to-red-500 bg-clip-text text-transparent break-words">
          Database Dashboard
        </h1>
        <button
          onClick={fetchDatabases}
          disabled={loading}
          className="px-4 py-2.5 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl hover:bg-white/70 dark:hover:bg-gray-700/70 flex items-center gap-2 transition-all duration-300 shadow-lg shadow-black/5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Statistics Cards */}
      {databaseInfo && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium truncate">
                  Database Name
                </p>
                <p className="text-lg sm:text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 dark:from-red-400 dark:to-red-500 bg-clip-text text-transparent break-words">
                  {databaseInfo.database}
                </p>
              </div>
              <div className="p-3 backdrop-blur-sm bg-red-500/20 dark:bg-red-600/20 rounded-xl">
                <Database className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium truncate">
                  Total Collections
                </p>
                <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400 break-words">
                  {databaseInfo.totalCollections}
                </p>
              </div>
              <div className="p-3 backdrop-blur-sm bg-blue-500/20 dark:bg-blue-600/20 rounded-xl">
                <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium truncate">
                  Total Documents
                </p>
                <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400 break-words">
                  {formatNumber(totalDocuments)}
                </p>
              </div>
              <div className="p-3 backdrop-blur-sm bg-green-500/20 dark:bg-green-600/20 rounded-xl">
                <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search collections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl focus:ring-2 focus:ring-red-500/50 dark:focus:ring-red-400/50 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 shadow-lg shadow-black/5"
          />
        </div>
      </div>

      {/* Collections List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading database information...</p>
        </div>
      ) : !databaseInfo ? (
        <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-12 text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Failed to load database information</p>
        </div>
      ) : filteredCollections.length === 0 ? (
        <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-12 text-center">
          <Database className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">
            {searchTerm ? "No collections found matching your search" : "No collections found"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCollections.map((collection) => (
            <button
              type="button"
              key={collection.name}
              onClick={() => navigate(`/admin/databases/${encodeURIComponent(collection.name)}`)}
              className="text-left backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 hover:scale-[1.02] cursor-pointer w-full"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="p-2.5 backdrop-blur-sm bg-red-500/20 dark:bg-red-600/20 rounded-xl flex-shrink-0">
                    <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate break-words">
                      {collection.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {collection.type}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-white/20 dark:border-gray-700/30">
                <span className="text-sm text-gray-600 dark:text-gray-400">Documents</span>
                {collection.error ? (
                  <span className="text-sm font-semibold text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Error
                  </span>
                ) : (
                  <span className="text-lg font-bold bg-gradient-to-r from-red-600 to-red-700 dark:from-red-400 dark:to-red-500 bg-clip-text text-transparent">
                    {formatNumber(collection.count)}
                  </span>
                )}
              </div>

              {collection.error && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-2 break-words">
                  {collection.error}
                </p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DatabaseDashboard;

